import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { snackbarService } from 'shared/lib/snackbar/service';
import type { ChartDataPayload, WidgetT } from 'shared/types';
import { RQAPICallWithData } from 'store/APIClient';

export const useAPICreateChart = (
    onSuccessAction?: (
        widget: WidgetT,
        redirect: boolean,
        dashboardSlug: string,
    ) => void,
) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const getSnackAction = (
        snackAction: boolean,
        key: number | undefined,
        slug: string,
    ) =>
        snackAction
            ? {
                  message: t('components:common.see'),
                  onClick: () => {
                      if (key) {
                          navigate(`/dashboard/${slug}#widget-${key}`);
                      } else {
                          navigate(`/dashboard/${slug}`);
                      }
                  },
              }
            : undefined;
    const mutation = useMutation({
        mutationFn: (payload: {
            dashboard: { key: number; slug: string };
            chartData: ChartDataPayload;
            redirect: boolean;
            snackAction: boolean;
            fromPosition?: FromPositionQueryT;
        }) => {
            const { chartData, fromPosition } = payload;
            if (fromPosition) {
                chartData.hasDefinedPosition = true;
                chartData.previousWidgetPosition = fromPosition;
            } else {
                chartData.hasDefinedPosition = false;
            }
            return RQAPICallWithData<WidgetT>({
                endpoint: `explore/dashboard/${payload.dashboard.key}/charts`,
                method: 'POST',
                body: { ...chartData },
            });
        },

        onSuccess: (response, { dashboard, redirect, snackAction }) => {
            queryClient.removeQueries({
                queryKey: ['getLayout', dashboard.slug, ''],
            });
            snackbarService.showInfo(
                response.content.message,
                getSnackAction(
                    snackAction,
                    response.content.data?.key,
                    dashboard.slug,
                ),
            );
            if (onSuccessAction)
                onSuccessAction(
                    response.content.data,
                    redirect,
                    dashboard.slug,
                );
        },
    });
    return { saveChartFromData: mutation.mutate };
};
